import type {
    EntityPatch,
    HistoryDomainAdapter,
    HistoryEntitySnapshot,
    HistoryPatchApplyInput,
} from '..';
import type { DatumAxisHistorySnapshot } from '../../reference';
import { DATUM_AXIS_HISTORY_ENTITY_ID } from '../../reference';
import { isSerializablePlainData } from './SerializablePlainData';

//------ 基准轴撤销重做 开始------
/**
 * 基准轴历史 adapter ID。
 */
export const DATUM_AXIS_HISTORY_ADAPTER_ID = 'history-domain:datum-axis';

/**
 * 基准轴历史运行时存储。
 */
export interface DatumAxisHistoryStore {
    /**
     * 恢复基准轴历史快照。
     *
     * @param snapshot 基准轴历史快照。
     */
    readonly restoreDatumAxisHistorySnapshot: (snapshot: DatumAxisHistorySnapshot) => void;
}

/**
 * 判断对象是否为普通对象。
 *
 * @param value 待判断对象。
 * @returns 是否为普通对象。
 */
function isPlainObject(value: object): boolean {
    const prototype = Object.getPrototypeOf(value);

    return prototype === Object.prototype || prototype === null;
}

/**
 * 递归移除历史快照中的 `undefined` 字段。
 *
 * @param value 待归一化值。
 * @returns 已移除 `undefined` 的值。
 */
function omitUndefinedDeep(value: unknown): unknown {
    if (Array.isArray(value)) {
        return value
            .map((item) => omitUndefinedDeep(item))
            .filter((item) => item !== undefined);
    }

    if (!value || typeof value !== 'object') {
        return value;
    }

    if (!isPlainObject(value)) {
        return value;
    }

    return Object.fromEntries(
        Object.entries(value as Record<string, unknown>)
            .map(([key, item]) => [key, omitUndefinedDeep(item)] as const)
            .filter(([, item]) => item !== undefined),
    );
}

/**
 * 归一化基准轴历史快照。
 *
 * @param snapshot 来源快照。
 * @returns 可序列化快照。
 */
function normalizeDatumAxisHistorySnapshot(snapshot: DatumAxisHistorySnapshot): DatumAxisHistorySnapshot {
    return omitUndefinedDeep(snapshot) as DatumAxisHistorySnapshot;
}

/**
 * 归一化基准轴实体快照。
 *
 * @param snapshot 来源实体快照。
 * @returns 可序列化实体快照。
 */
function normalizeDatumAxisEntitySnapshot(
    snapshot: HistoryEntitySnapshot<DatumAxisHistorySnapshot>,
): HistoryEntitySnapshot<DatumAxisHistorySnapshot> {
    if (!snapshot.exists || !snapshot.data) {
        return snapshot;
    }

    return {
        ...snapshot,
        data: normalizeDatumAxisHistorySnapshot(snapshot.data),
    };
}

/**
 * 判断基准轴快照是否为 plain data。
 *
 * @param snapshot 基准轴快照。
 * @returns 是否为可序列化 plain data。
 */
export function isPlainDatumAxisHistorySnapshot(snapshot: DatumAxisHistorySnapshot): boolean {
    const normalizedSnapshot = normalizeDatumAxisHistorySnapshot(snapshot);

    return typeof normalizedSnapshot.managerId === 'string'
        && Array.isArray(normalizedSnapshot.axes)
        && Array.isArray(normalizedSnapshot.features)
        && isSerializablePlainData(normalizedSnapshot);
}

/**
 * 应用基准轴历史快照。
 *
 * @param input 补丁应用输入。
 */
function applyDatumAxisSnapshot(
    input: HistoryPatchApplyInput<DatumAxisHistorySnapshot, DatumAxisHistoryStore>,
): void {
    const snapshot = input.snapshot;

    if (!snapshot.exists) {
        input.context.restoreDatumAxisHistorySnapshot({
            managerId: DATUM_AXIS_HISTORY_ENTITY_ID,
            axes: [],
            features: [],
        });
        return;
    }

    if (!snapshot.data) {
        throw new Error(`基准轴 ${input.patch.entityId} 缺少快照数据。`);
    }

    const normalizedSnapshot = normalizeDatumAxisHistorySnapshot(snapshot.data);
    if (!isPlainDatumAxisHistorySnapshot(normalizedSnapshot)) {
        throw new Error(`基准轴 ${input.patch.entityId} 的快照不是可序列化 plain data。`);
    }

    input.context.restoreDatumAxisHistorySnapshot(normalizedSnapshot);
}

/**
 * 比较基准轴历史快照。
 *
 * @param left 左侧快照。
 * @param right 右侧快照。
 * @returns 是否语义相等。
 */
function areDatumAxisSnapshotsEqual(
    left: HistoryEntitySnapshot<DatumAxisHistorySnapshot>,
    right: HistoryEntitySnapshot<DatumAxisHistorySnapshot>,
): boolean {
    return JSON.stringify(left) === JSON.stringify(right);
}

/**
 * 创建基准轴历史 adapter。
 *
 * @returns 基准轴历史 adapter。
 */
export function createDatumAxisHistoryAdapter(): HistoryDomainAdapter<
    DatumAxisHistorySnapshot,
    DatumAxisHistoryStore
> {
    return {
        adapterId: DATUM_AXIS_HISTORY_ADAPTER_ID,
        kind: 'datum',
        applySnapshot: applyDatumAxisSnapshot,
        areSnapshotsEqual: areDatumAxisSnapshotsEqual as HistoryDomainAdapter<
            DatumAxisHistorySnapshot,
            DatumAxisHistoryStore
        >['areSnapshotsEqual'],
    };
}

/**
 * 创建基准轴历史补丁。
 *
 * @param patch 补丁字段。
 * @returns 基准轴历史补丁。
 */
export function createDatumAxisHistoryPatch(
    patch: Omit<EntityPatch<DatumAxisHistorySnapshot>, 'adapterId' | 'entityKind'>,
): EntityPatch<DatumAxisHistorySnapshot> {
    return {
        ...patch,
        before: normalizeDatumAxisEntitySnapshot(patch.before),
        after: normalizeDatumAxisEntitySnapshot(patch.after),
        entityKind: 'datum',
        adapterId: DATUM_AXIS_HISTORY_ADAPTER_ID,
    };
}
//------ 基准轴撤销重做 结束------
